import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FranchiseFeatureType } from 'app/main/apps/franchises/type';
import { IpInfoType } from 'app/services/ipInfoService';
import jwtService from 'app/services/jwtService';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import { setDefaultSettings } from 'app/store/fuse/settingsSlice';
import i18n from 'i18n';
import { RoleEnum, UserRolesType } from '../authRoles';
import { UserDataState, UserState } from './type';
import { userLocationSelector } from './user.selector';

export const setUserData =
  ({
    role,
    userData,
    reachableFranchisesCount,
  }: {
    role?: UserRolesType;
    userData: Partial<UserDataState>;
    reachableFranchisesCount?: number;
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const userIpInfo = userLocationSelector(getState());
    /* Setrole User Settings */
    dispatch(
      setDefaultSettings({
        layout: {
          style: 'layout1',
          config: {
            scroll: 'content',
            navbar: {
              display: true,
              folded: true,
              position: 'left',
            },
            toolbar: {
              display: true,
              style: 'fixed',
              position: 'below',
            },
            footer: {
              display: true,
              style: 'fixed',
              position: 'below',
            },
            mode: 'fullwidth',
          },
        },
        customScrollbars: true,
        theme: {
          main: 'default',
          navbar: 'default',
          toolbar: 'default',
          footer: 'default',
        },
      }),
    ); // user.data.settings

    dispatch(
      setUser({
        role,
        ipInfo: userIpInfo,
        reachableFranchisesCount,
        data: {
          ...userData,
          displayName: [userData.firstName, userData.lastName].filter(Boolean).join(' '),
          photoURL: 'assets/images/avatars/Abbott.jpg',
          email: userData.email || '',
          id: userData.id || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          isSubscribedRetailer: userData.isSubscribedRetailer || false,
          hasFullAccess: userData.hasFullAccess || false,
          dateCreated: userData.dateCreated || '',
          hasPassword: userData.hasPassword || false,
          isAdmin: userData.isAdmin || false,
          acl: userData.acl,
        },
      }),
    ); // user
  };

export const updateUser = (user: UserState) => async (dispatch: AppDispatch) => {
  if (!user.role || user.role.length === 0) {
    // is guest
    return;
  }
  jwtService
    .updateUser(user)
    .then(() => {
      dispatch(showMessage({ message: i18n.t('pages.account.alert.updateSuccessfully'), variant: 'success' }));
    })
    .catch(error => {
      dispatch(showMessage({ message: error.message }));
    });
};

const initialState: UserState = {
  role: undefined, // guest
  ipInfo: undefined,
  data: {
    displayName: '',
    photoURL: '',
    email: '',
    id: '',
    firstName: '',
    lastName: '',
    isSubscribedRetailer: false,
    hasFullAccess: false,
    dateCreated: '',
    hasPassword: false,
    isAdmin: false,
    acl: {
      role: RoleEnum.STORE_OWNER,
    },
  },
};

const userSlice = createSlice({
  name: 'auth/user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserState>) => action.payload,
    setUserLocation: (state: UserState, action: PayloadAction<IpInfoType>) => {
      state.ipInfo = action.payload;
    },
    userLoggedOut: () => initialState,
    setUserPermissions: (state: UserState, action: PayloadAction<FranchiseFeatureType[]>) => {
      state.permissions = action.payload;
    },
  },
});

export const { setUser, setUserLocation, userLoggedOut, setUserPermissions } = userSlice.actions;

export default userSlice.reducer;
